import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { usePage } from "@danishagro/shared/src/contexts/page.context";
import { getPage } from "@helpers/getPage.helper";
import { DA_Hero } from "@danishagro/shared/src/components/organisms/Hero/Hero.component";
import ComponentErrorBoundary from "@danishagro/shared/src/components/molecules/ErrorBoundary/ComponentErrorBoundary.component";
import { DA_NewPageData } from "@interfaces/newPageData.interface";
import { getAbsoluteUrl } from "@danishagro/shared/src/components/organisms/SiteHeader/LanguageSelector/hooks/getAbsolutePath.hook";
import { HeroObj } from "@danishagro/shared/src/interfaces/HeroObj.interface";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DynamicBlocksRenderer } from "@helpers/DynamicBlocksRenderer.helper";

const DA_HomePage = () => {
    const { setPageIsLoading } = usePage();
    const [pageData, setPageData] = useState<DA_NewPageData>();
    const [heroData, setHeroData] = useState<HeroObj>();
    const [heroImage, setHeroImage] = useState(null);
    const [heroVideo, setHeroVideo] = useState(null);
    const [heroButton, setHeroButton] = useState(null);
    const [heroPosterStart, setHeroPosterStart] = useState(null);

    const { heroObj, customerNumber } = useAppData();

    const getImageObject = (data: HeroObj) => {
        const image = data?.props?.image;

        if (!image || !image.props?.umbracoFile) {
            // Return null if the image or umbracoFile is missing
            return null;
        }

        if (image && image?.props?.umbracoFile?.includes("assetID")) {
            const imageWidth: number = 1400;
            const imageHeight: number = 600;
            const imageQuality: number = 80;

            const digizuiteSrc = image?.props?.umbracoFile
                .replace("{QUALITY}", `${imageQuality}`)
                .replace("{WIDTH}", `${imageWidth}`)
                .replace("{HEIGHT}", `${imageHeight}`);

            return {
                url: digizuiteSrc,
                width: imageWidth,
                height: imageHeight,
                alt: image.name,
            };
        } else if (image && image?.props?.umbracoFile) {
            return {
                url: getAbsoluteUrl(image?.props?.umbracoFile),
                width: image.props?.umbracoWidth,
                height: image.props?.umbracoHeight,
                alt: image.name,
            };
        } else {
            return null; // or handle the case where no image is available
        }
    };

    const getVideoObject = (data: HeroObj) => {
        const video = data?.props?.video;

        if (video && video.length > 0 && video[0]?.content?.props?.videoFile) {
            const videoContent = video[0].content;
            const videoProps = videoContent.props;
            const videoUrl = videoProps.videoFile.url;

            let posterStart = null;
            if (videoProps.posterStart) {
                const poster = videoProps.posterStart;
                posterStart = {
                    url: poster.url,
                    width: poster.props?.umbracoWidth
                        ? parseInt(poster.props.umbracoWidth)
                        : undefined,
                    height: poster.props?.umbracoHeight
                        ? parseInt(poster.props.umbracoHeight)
                        : undefined,
                    alt: poster.name,
                };
            }

            return {
                video: {
                    alias: videoContent.alias,
                    url: videoUrl,
                    autoplay: true,
                    controls: false,
                    muted: true,
                    loop: false,
                },
                posterStart: posterStart,
            };
        } else {
            return null;
        }
    };

    useEffect(() => {
        setPageIsLoading(true);

        // set hero data from global hero object
        const heroData = heroObj;
        setHeroData(heroData);
        setHeroImage(getImageObject(heroData));
        setHeroButton(heroData?.props?.buttons?.[0]?.content?.props || null);
        const videoData = getVideoObject(heroData);
        if (videoData) {
            setHeroVideo(videoData.video);
            setHeroPosterStart(videoData.posterStart);
        } else {
            setHeroVideo(null);
            setHeroPosterStart(null);
        }

        getPage()
            .then((page) => {
                setPageData(page);
                setPageIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching page content:", error);
                setPageIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setPageIsLoading, customerNumber]);

    return (
        <>
            <Helmet>
                <title>{pageData?.name}</title>
                <meta name="description" content={pageData?.metaDescription} />
            </Helmet>

            <div className="frontpage">
                <div className="frontpage-content">
                    {/* hero */}

                    {heroData && (
                        <div className="frontpage-hero">
                            <ComponentErrorBoundary showNothing componentName="Hero">
                                <DA_Hero
                                    headline={heroData.props?.headline || ""}
                                    text={heroData.props?.text || ""}
                                    image={
                                        heroImage
                                            ? {
                                                  src: heroImage.url,
                                                  width: heroImage.width,
                                                  height: heroImage.height,
                                                  alt: heroImage.alt,
                                              }
                                            : undefined
                                    }
                                    video={heroVideo}
                                    posterStart={heroPosterStart}
                                    alias={heroData.alias}
                                    link={{
                                        title: heroButton?.label || heroButton?.link?.name || "",
                                        href: heroButton?.link?.url || "",
                                        target: heroButton?.link?.target || "_self",
                                        conventionalRouting: true,
                                    }}
                                    fullWidthMobile
                                    enableTracking={true}
                                />
                            </ComponentErrorBoundary>
                        </div>
                    )}

                    {pageData?.props?.modules && (
                        <DynamicBlocksRenderer
                            blocks={pageData.props.modules.map((mod: any) => mod.content)}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default DA_HomePage;
